import React, { useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

// Coerces a numeric TextField's raw string value into something the backend's
// PositiveIntegerField (+ optional MinValueValidator) will actually accept, rather than
// letting an out-of-range value round-trip into a 400. `min` should match the backend
// field's floor (e.g. 1 for CapSettings.cap, 0 elsewhere). An empty string becomes `null`
// for allowNull fields (no limit) or `min` otherwise; any other value is rounded to the
// nearest integer and clamped up to `min`. Returns `undefined` for unparseable input,
// which signals the caller to leave the field's value untouched.
export const coerceFieldValue = (raw, allowNull, min = 0) => {
    if (raw === '') return allowNull ? null : min
    const num = Number(raw)
    if (Number.isNaN(num)) return undefined
    return Math.max(min, Math.round(num))
}

// One family's card: an enable switch plus its numeric fields. `fields` describes each
// numeric field as [key, label, allowNull, min] — see `coerceFieldValue` for how a raw
// input value maps to the stored value.
const SettingsCard = ({ title, description, value, onChange, fields }) => {
    const setField = (key, allowNull, min = 0) => (event) => {
        const coerced = coerceFieldValue(event.target.value, allowNull, min)
        if (coerced === undefined) return
        onChange({ ...value, [key]: coerced })
    }

    const descriptionId = `${title.replace(/\W+/g, '-')}-description`

    return (
        <Paper sx={{ padding: 2, marginBottom: 2 }} elevation={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='h6'>{title}</Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={value.enabled}
                            onChange={(event) => onChange({ ...value, enabled: event.target.checked })}
                            inputProps={{
                                'aria-label': `${title} enabled`,
                                ...(description && { 'aria-describedby': descriptionId }),
                            }}
                        />
                    }
                    label={value.enabled ? 'Enabled' : 'Disabled'}
                />
            </Box>
            {description && (
                <Typography id={descriptionId} variant='body2' color='text.secondary'>
                    {description}
                </Typography>
            )}
            <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap sx={{ marginTop: 1 }}>
                {fields.map(([key, label, allowNull, min = 0]) => (
                    <TextField
                        key={key}
                        type='number'
                        size='small'
                        label={label}
                        value={value[key] === null || value[key] === undefined ? '' : value[key]}
                        onChange={setField(key, allowNull, min)}
                        helperText={allowNull ? 'Blank = no limit' : undefined}
                        inputProps={{ min, step: 1 }}
                        sx={{ minWidth: 160 }}
                    />
                ))}
            </Stack>
        </Paper>
    )
}

// The Configuration tab: one form covering every constraint's enable flag and tunable
// thresholds, saved in a single PUT. `settings` is the fetched payload ({ expiry,
// coverage, citation, link_check, retraction, caps }); `onSave` dispatches the save thunk.
const ConstraintSettings = ({ settings, onSave }) => {
    const [draft, setDraft] = useState(settings)
    const [saving, setSaving] = useState(false)

    // Reinitialize the draft whenever a fresh payload arrives (initial load, or after a
    // successful save echoes the server's persisted values back).
    useEffect(() => {
        setDraft(settings)
    }, [settings])

    if (!draft) return null

    const handleSave = async () => {
        setSaving(true)
        await onSave(draft)
        setSaving(false)
    }

    return (
        <Box>
            <SettingsCard
                title='Expiry (resource age limits)'
                value={draft.expiry}
                onChange={(expiry) => setDraft({ ...draft, expiry })}
                fields={[
                    ['book_max_age_years', 'Book max age (years)', true],
                    ['media_max_age_years', 'Media max age (years)', true],
                    ['paper_max_age_years', 'Paper max age (years)', true],
                ]}
            />
            <SettingsCard
                title='Coverage (minimum resources per tag)'
                value={draft.coverage}
                onChange={(coverage) => setDraft({ ...draft, coverage })}
                fields={[
                    ['min_resources_per_content_tag', 'Min resources / content tag', false],
                    ['min_reviews_per_specialty_tag', 'Min reviews / specialty tag', false],
                ]}
            />
            <SettingsCard
                title='Citations (guideline paper floor)'
                value={draft.citation}
                onChange={(citation) => setDraft({ ...draft, citation })}
                fields={[['min_citations', 'Min citations', false]]}
            />
            {/* Absent when talking to a backend that predates the link_check switch. */}
            {draft.link_check && (
                <SettingsCard
                    title='Broken links (link check)'
                    value={draft.link_check}
                    onChange={(link_check) => setDraft({ ...draft, link_check })}
                    fields={[]}
                />
            )}
            {/* Absent when talking to a backend that predates the retraction switch. */}
            {draft.retraction && (
                <SettingsCard
                    title='Retraction screening'
                    description='Flags retracted hub papers and drops retracted replacement candidates, via Crossref.'
                    value={draft.retraction}
                    onChange={(retraction) => setDraft({ ...draft, retraction })}
                    fields={[]}
                />
            )}
            {draft.caps.map((cap, index) => (
                <SettingsCard
                    key={cap.constraint_key}
                    title={cap.label || cap.constraint_key}
                    value={cap}
                    onChange={(next) => {
                        const caps = [...draft.caps]
                        caps[index] = next
                        setDraft({ ...draft, caps })
                    }}
                    fields={[['cap', 'Cap', false, 1]]}
                />
            ))}
            <Button variant='contained' onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save configuration'}
            </Button>
        </Box>
    )
}

export default ConstraintSettings
